/**
 * Guards de rutas:
 *  - ProtectedRoute: redirige a /login si no hay sesión.
 *  - GuestOnlyRoute: lo opuesto — redirige al dashboard apropiado si hay sesión.
 */
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { Lock, PackageOpen } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-400 text-sm">Cargando…</div>
    </div>
  );
}

/** Con sesión, el sitio de cualquiera es la raíz: el escritorio. */
const DESTINO_CON_SESION = "/";

export function ProtectedRoute() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <LoadingScreen />;
  if (status === "guest") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

export function GuestOnlyRoute() {
  const { status, me } = useAuth();

  if (status === "loading") return <LoadingScreen />;
  if (status === "authenticated" && me) {
    return <Navigate to={DESTINO_CON_SESION} replace />;
  }
  return <Outlet />;
}

/**
 * Exige un permiso para entrar a la ruta.
 *
 * Hasta ahora solo el sidebar filtraba por permisos: ocultaba el link, pero
 * escribiendo la URL cualquiera entraba y la página se rompía a pedazos con
 * 403 de la API. Ocultar no es lo mismo que impedir. Usa los MISMOS códigos
 * que `nav-items.ts` para que menú y router no puedan discrepar.
 */
/**
 * Exige que el policlínico TENGA contratado el módulo.
 *
 * El menú ya esconde lo que no está, pero un menú escondido no impide escribir
 * la URL, y quien la escribe se encontraba una pantalla que carga y luego se
 * llena de errores porque la API responde 404 a todo. Esto lo dice de una.
 *
 * Es distinto de [RequirePermission] a propósito, y por eso el texto no habla
 * de roles ni de pedirle permiso a nadie: aquí no falta un permiso, falta el
 * módulo. Mandar a alguien a pedirle a su administrador algo que su
 * administrador tampoco puede darle es hacerle perder la tarde.
 */
export function RequireFuncionalidad({ codigo }: { codigo: string }) {
  const { status, me } = useAuth();

  if (status === "loading") return <LoadingScreen />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.is_super_admin || me.funcionalidades.includes(codigo)) return <Outlet />;

  return (
    <div className="mx-auto max-w-lg py-16 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
        <PackageOpen className="h-5 w-5 text-slate-400" aria-hidden="true" />
      </div>
      <h1 className="text-lg font-semibold text-slate-900">
        Este módulo no está activo
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Tu policlínico no tiene contratado este módulo. No es cosa de tu rol:
        aquí no existe para nadie.
      </p>
      <Link
        to="/"
        className="mt-6 inline-block text-sm font-medium text-[color:var(--color-link)] hover:text-[color:var(--color-link)]"
      >
        Volver al inicio
      </Link>
    </div>
  );
}

export function RequirePermission({ codigo }: { codigo: string }) {
  const { status, me } = useAuth();

  if (status === "loading") return <LoadingScreen />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.is_super_admin || me.permisos.includes(codigo)) return <Outlet />;

  // Mensaje explícito en vez de un redirect silencioso: si alguien llegó acá
  // por un link, tiene que entender por qué no puede pasar.
  return (
    <div className="mx-auto max-w-lg py-16 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
        <Lock className="h-5 w-5 text-slate-400" aria-hidden="true" />
      </div>
      <h1 className="text-lg font-semibold text-slate-900">No tienes acceso a esta sección</h1>
      <p className="mt-1 text-sm text-slate-500">
        Tu rol no incluye el permiso <code className="font-mono text-xs">{codigo}</code>. Si lo
        necesitas, pídeselo al administrador del policlínico.
      </p>
      <Link
        to="/"
        className="mt-6 inline-block text-sm font-medium text-[color:var(--color-link)] hover:text-[color:var(--color-link)]"
      >
        Volver al inicio
      </Link>
    </div>
  );
}

